<template>
  <Dialog
    v-model:visible="visible"
    modal
    header="User Guide"
    :style="{ width: '60rem' }"
    :breakpoints="{ '1199px': '80vw', '575px': '95vw' }"
    class="user-guide-dialog"
    @show="onShow"
    @hide="onHide"
  >
    <div class="user-guide-content">
      <div
        class="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900 dark:to-primary-800 rounded-xl p-6 mb-6 border border-primary-200 dark:border-primary-700"
      >
        <div class="flex justify-between items-center gap-4 flex-wrap">
          <div class="flex-1 min-w-[250px]">
            <h2
              class="text-2xl font-bold text-primary-700 dark:text-primary-200 mb-2 flex items-center"
            >
              <i class="pi pi-book mr-2"></i>
              Welcome to MyPerformance Help
            </h2>
            <p class="text-primary-600 dark:text-primary-300 text-base leading-relaxed m-0">
              Find answers, learn features, and get the most out of your platform
            </p>
          </div>
          <div class="flex gap-6">
            <div
              class="flex items-center gap-2 text-primary-600 dark:text-primary-300 font-medium text-sm"
            >
              <i class="pi pi-file-text text-base"></i>
              <span>10 Topics</span>
            </div>
            <div
              class="flex items-center gap-2 text-primary-600 dark:text-primary-300 font-medium text-sm"
            >
              <i class="pi pi-clock text-base"></i>
              <span>5 min read</span>
            </div>
          </div>
        </div>
      </div>

      <Tabs v-model:value="activeTab" class="guide-tabs">
        <TabList>
          <Tab :value="0"><i class="pi pi-home"></i>Getting Started</Tab>
          <Tab :value="1"><i class="pi pi-chart-line"></i>Retailer Development Actions</Tab>
          <Tab :value="2"><i class="pi pi-file-edit"></i>Reporting</Tab>
          <Tab :value="3"><i class="pi pi-cog"></i>Settings</Tab>
          <Tab :value="4"><i class="pi pi-keyboard"></i>Tips &amp; Shortcuts</Tab>
          <Tab :value="5"><i class="pi pi-life-ring"></i>Support</Tab>
        </TabList>
        <TabPanels>
        <TabPanel :value="0">
          <div class="guide-section">
            <h3 class="section-title mt-4">
              <i class="pi pi-home mr-2"></i>
              Getting Started
            </h3>
            <div class="guide-item">
              <h4>Welcome to MyPerformance</h4>
              <p>
                This platform helps you track and manage your performance goals, retailer
                development actions, and achievements. Use the navigation menu to access different
                sections of the application.
              </p>
            </div>
            <div class="guide-item">
              <h4>Navigation</h4>
              <p>Use the top navigation bar to switch between different sections:</p>
              <ul>
                <li><strong>Reporting:</strong> View performance reports and analytics</li>
                <li>
                  <strong>Retailer Development Actions:</strong> Create and manage your retailer
                  development actions
                </li>
              </ul>
            </div>
            <div class="guide-item">
              <h4>Quick Start</h4>
              <div class="quick-start-cards">
                <div class="quick-start-card clickable" @click="scrollToSection('action-plans')">
                  <div class="card-icon">
                    <i class="pi pi-plus-circle hover:text-white"></i>
                  </div>
                  <h5>Create Your First Plan</h5>
                  <p>Start by creating an retailer development action to track your goals</p>
                  <div class="card-arrow">
                    <i class="pi pi-arrow-right"></i>
                  </div>
                </div>
                <div class="quick-start-card clickable" @click="scrollToSection('tracking')">
                  <div class="card-icon">
                    <i class="pi pi-chart-line hover:text-white"></i>
                  </div>
                  <h5>Track Progress</h5>
                  <p>Monitor your performance with visual indicators</p>
                  <div class="card-arrow">
                    <i class="pi pi-arrow-right"></i>
                  </div>
                </div>
                <div class="quick-start-card clickable" @click="scrollToSection('notifications')">
                  <div class="card-icon">
                    <i class="pi pi-bell hover:text-white"></i>
                  </div>
                  <h5>Stay Updated</h5>
                  <p>Get notified about important updates and deadlines</p>
                  <div class="card-arrow">
                    <i class="pi pi-arrow-right"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>

        <TabPanel :value="1">
          <div class="guide-section">
            <h3 class="section-title mt-4">
              <i class="pi pi-chart-line mr-2"></i>
              Retailer Development Actions
            </h3>
            <div class="guide-item">
              <h4>Creating Retailer Development Actions</h4>
              <p>Follow these steps to create your first retailer development action:</p>
              <div class="step-guide">
                <div class="step-item">
                  <div class="step-number">1</div>
                  <div class="step-content">
                    <h5>Navigate to Retailer Development Actions</h5>
                    <p>Click on "Retailer Development Actions" in the main navigation menu</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">2</div>
                  <div class="step-content">
                    <h5>Click "New Plan"</h5>
                    <p>Look for the "New Plan" button in the top-right corner</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">3</div>
                  <div class="step-content">
                    <h5>Fill in Details</h5>
                    <p>Enter your plan name, description, and objectives</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">4</div>
                  <div class="step-content">
                    <h5>Set Timeline</h5>
                    <p>Choose start and end dates for your plan</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">5</div>
                  <div class="step-content">
                    <h5>Save & Start</h5>
                    <p>Click "Save" to create your plan and begin tracking</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="guide-item">
              <h4>Managing Actions</h4>
              <p>Within each retailer development action, you can:</p>
              <ul>
                <li>Add new actions and tasks</li>
                <li>Set priorities and deadlines</li>
                <li>Track progress and completion status</li>
                <li>Add comments and notes</li>
                <li>Attach relevant documents</li>
              </ul>
            </div>
            <div class="guide-item">
              <h4>Best Practices</h4>
              <div class="tip-box">
                <i class="pi pi-lightbulb"></i>
                <div>
                  <strong>Pro Tip:</strong> Break down large goals into smaller, manageable actions.
                  This makes tracking progress easier and keeps you motivated.
                </div>
              </div>
            </div>
          </div>
        </TabPanel>

        <TabPanel :value="2">
          <div class="guide-section">
            <h3 class="section-title mt-4">
              <i class="pi pi-file-edit mr-2"></i>
              Reporting
            </h3>
            <div class="guide-item">
              <h4>Report Features</h4>
              <div class="quick-start-cards">
                <div class="quick-start-card">
                  <div class="card-icon">
                    <i class="pi pi-money-bill"></i>
                  </div>
                  <h5>Commission Reports</h5>
                  <p>View detailed commission statements and calculations</p>
                </div>
                <div class="quick-start-card">
                  <div class="card-icon">
                    <i class="pi pi-shield"></i>
                  </div>
                  <h5>Insurance Tracking</h5>
                  <p>Monitor insurance policy metrics and performance</p>
                </div>
                <div class="quick-start-card">
                  <div class="card-icon">
                    <i class="pi pi-star"></i>
                  </div>
                  <h5>Performance Tracking</h5>
                  <p>Track performance-based metrics and calculations</p>
                </div>
              </div>
            </div>
            <div class="guide-item">
              <h4>Generating Reports</h4>
              <p>Follow these steps to create and view your statements:</p>
              <div class="step-guide">
                <div class="step-item">
                  <div class="step-number">1</div>
                  <div class="step-content">
                    <h5>Select Report Type</h5>
                    <p>
                      Choose from Commission, Insurance, VAT Notes, Performance Tracking, or MAIA
                      Recharge
                    </p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">2</div>
                  <div class="step-content">
                    <h5>Choose Scope</h5>
                    <p>Select between Retailer or Group level reporting</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">3</div>
                  <div class="step-content">
                    <h5>Select Entity</h5>
                    <p>Choose specific Retailer or Group from available options</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">4</div>
                  <div class="step-content">
                    <h5>View Report</h5>
                    <p>Review your generated report with detailed data and metrics</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="guide-item">
              <h4>Report Types Explained</h4>
              <ul>
                <li>
                  <strong>Commission Reports:</strong> Detailed breakdown of commission
                  calculations, earnings, and payment schedules
                </li>
                <li>
                  <strong>Insurance Tracking:</strong> Policy performance metrics, coverage details,
                  and compliance tracking
                </li>
                <li>
                  <strong>VAT Notes:</strong> Value-added tax documentation and calculations for
                  financial reporting
                </li>
                <li>
                  <strong>Performance Tracking:</strong> Performance-based metrics, calculations,
                  and achievement tracking
                </li>
                <li>
                  <strong>MAIA Recharge:</strong> VAT Invoice specifically for Retailers MAIA
                  subscription fee
                </li>
              </ul>
            </div>
            <!-- <div class="guide-item">
              <h4>MAIA Recharge Statements</h4>
              <p>
                Use MAIA Recharge statements to review recharge entries for the selected scope and
                period.
              </p>
              <div class="step-guide">
                <div class="step-item">
                  <div class="step-number">1</div>
                  <div class="step-content">
                    <h5>Open Reporting</h5>
                    <p>Navigate to Reporting from the main menu.</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">2</div>
                  <div class="step-content">
                    <h5>Select MAIA Recharge</h5>
                    <p>Choose MAIA Recharge from the report type dropdown.</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">3</div>
                  <div class="step-content">
                    <h5>Set Scope and Entity</h5>
                    <p>Select Retailer or Group, then choose the relevant entity.</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">4</div>
                  <div class="step-content">
                    <h5>Review or Export</h5>
                    <p>Review the statement rows in-app and export if needed for reconciliation.</p>
                  </div>
                </div>
              </div>
            </div> -->
            <div class="guide-item">
              <h4>Retailer Drilldown Reports</h4>
              <p>
                In the Performance Tracking report, you can drill down into individual Retailer data
                for detailed analysis:
              </p>
              <ul>
                <li>
                  <strong>Click on a Row:</strong> Click any Retailer row in the Performance
                  Tracking table to open a detailed drilldown view
                </li>
                <li>
                  <strong>Finance Cases:</strong> View all financed deals for the selected Retailer,
                  including customer details and agreement types
                </li>
                <li>
                  <strong>Accepted Not Funded Cases:</strong> Review deals that were accepted but
                  not yet funded, with proposal dates and customer information
                </li>
                <li>
                  <strong>Cancelled/Early Settlements:</strong> Examine cancelled deals and early
                  settlements for the selected Retailer
                </li>
              </ul>
              <div class="tip-box">
                <i class="pi pi-lightbulb"></i>
                <div>
                  <strong>Pro Tip:</strong> Use the Retailer drilldown feature to identify patterns
                  and opportunities for improvement in your performance metrics. Each tab provides
                  detailed insights into different aspects of the Retailer's performance.
                </div>
              </div>
            </div>
            <div class="guide-item">
              <h4>Best Practices</h4>
              <div class="tip-box">
                <i class="pi pi-lightbulb"></i>
                <div>
                  <strong>Pro Tip:</strong> Use the configuration summary to verify your settings
                  before generating reports. This ensures you're viewing the correct data for your
                  selected scope and entity.
                </div>
              </div>
            </div>
          </div>
        </TabPanel>

        <TabPanel :value="3">
          <div class="guide-section">
            <h3 class="section-title mt-4">
              <i class="pi pi-cog mr-2"></i>
              Settings
            </h3>
            <div class="guide-item">
              <h4>Quick Access Features</h4>
              <div class="quick-start-cards">
                <div class="quick-start-card" @click="scrollToSection('notifications')">
                  <div class="card-icon">
                    <i class="pi pi-bell"></i>
                  </div>
                  <h5>Notifications</h5>
                  <p>Stay informed about important updates and deadlines</p>
                </div>
                <div class="quick-start-card">
                  <div class="card-icon">
                    <i class="pi pi-palette"></i>
                  </div>
                  <h5>Theme Customisation</h5>
                  <p>Choose from multiple themes to suit your preference</p>
                </div>
                <div class="quick-start-card">
                  <div class="card-icon">
                    <i class="pi pi-eye"></i>
                  </div>
                  <h5>Accessibility</h5>
                  <p>Customise display settings for better usability</p>
                </div>
              </div>
            </div>
            <div class="guide-item">
              <h4>Managing Notifications</h4>
              <p>Follow these steps to manage your notifications:</p>
              <div class="step-guide">
                <div class="step-item">
                  <div class="step-number">1</div>
                  <div class="step-content">
                    <h5>Access Notifications</h5>
                    <p>Click the bell icon in the top navigation bar</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">2</div>
                  <div class="step-content">
                    <h5>View Details</h5>
                    <p>Review notification summaries and timestamps</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">3</div>
                  <div class="step-content">
                    <h5>Clear or Dismiss</h5>
                    <p>Clear individual items or all notifications at once</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="guide-item">
              <h4>Customising Settings</h4>
              <p>Personalise your experience with these options:</p>
              <ul>
                <li><strong>Themes:</strong> Choose from Blue, Anthracite, or White themes</li>
                <li>
                  <strong>Accessibility:</strong> Enable high contrast, dyslexia-friendly fonts, and
                  more
                </li>
                <li>
                  <strong>Typography:</strong> Adjust font size, line height, and letter spacing
                </li>
                <li>
                  <strong>Navigation:</strong> Enable keyboard navigation and focus indicators
                </li>
                <li><strong>Motion:</strong> Reduce animations for better accessibility</li>
              </ul>
            </div>
            <div class="guide-item">
              <h4>Best Practices</h4>
              <div class="tip-box">
                <i class="pi pi-lightbulb"></i>
                <div>
                  <strong>Pro Tip:</strong> Configure your accessibility settings when you first log
                  in to ensure the best experience for your needs. Your preferences are saved
                  automatically.
                </div>
              </div>
            </div>
          </div>
        </TabPanel>

        <TabPanel :value="4">
          <div class="guide-section">
            <h3 class="section-title mt-4">
              <i class="pi pi-keyboard mr-2"></i>
              Tips & Shortcuts
            </h3>
            <div class="guide-item">
              <h4>Productivity Features</h4>
              <div class="quick-start-cards">
                <div class="quick-start-card">
                  <div class="card-icon">
                    <i class="pi pi-bolt"></i>
                  </div>
                  <h5>Keyboard Shortcuts</h5>
                  <p>Navigate faster with powerful keyboard combinations</p>
                </div>
                <div class="quick-start-card">
                  <div class="card-icon">
                    <i class="pi pi-star"></i>
                  </div>
                  <h5>Best Practices</h5>
                  <p>Learn proven strategies for success</p>
                </div>
                <div class="quick-start-card">
                  <div class="card-icon">
                    <i class="pi pi-chart-bar"></i>
                  </div>
                  <h5>Performance Tips</h5>
                  <p>Optimise your workflow and tracking methods</p>
                </div>
              </div>
            </div>
            <div class="guide-item">
              <h4>Essential Keyboard Shortcuts</h4>
              <p>Master these shortcuts to navigate faster:</p>
              <div class="shortcuts-grid">
                <div class="shortcut-item">
                  <div class="shortcut-key">
                    <kbd>Ctrl + /</kbd>
                  </div>
                  <div class="shortcut-info">
                    <span class="shortcut-action">Open this help guide</span>
                    <span class="shortcut-category">Help</span>
                  </div>
                </div>
                <div class="shortcut-item">
                  <div class="shortcut-key">
                    <kbd>Ctrl + K</kbd>
                  </div>
                  <div class="shortcut-info">
                    <span class="shortcut-action">Focus search</span>
                    <span class="shortcut-category">Search</span>
                  </div>
                </div>
                <div class="shortcut-item">
                  <div class="shortcut-key">
                    <kbd>Esc</kbd>
                  </div>
                  <div class="shortcut-info">
                    <span class="shortcut-action">Close dialogs and popups</span>
                    <span class="shortcut-category">Navigation</span>
                  </div>
                </div>
                <div class="shortcut-item">
                  <div class="shortcut-key">
                    <kbd>Tab</kbd>
                  </div>
                  <div class="shortcut-info">
                    <span class="shortcut-action">Navigate between fields</span>
                    <span class="shortcut-category">Navigation</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="guide-item">
              <h4>Performance Tracking Best Practices</h4>
              <p>Follow these proven strategies for effective performance management:</p>
              <div class="step-guide">
                <div class="step-item">
                  <div class="step-number">1</div>
                  <div class="step-content">
                    <h5>Set SMART Goals</h5>
                    <p>
                      Create Specific, Measurable, Achievable, Relevant, and Time-bound objectives
                    </p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">2</div>
                  <div class="step-content">
                    <h5>Review Regularly</h5>
                    <p>Schedule weekly check-ins to review progress and adjust plans</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">3</div>
                  <div class="step-content">
                    <h5>Document Progress</h5>
                    <p>Add comments and notes to track learnings and achievements</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">4</div>
                  <div class="step-content">
                    <h5>Stay Consistent</h5>
                    <p>Update action statuses promptly to maintain accurate tracking</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="guide-item">
              <h4>Productivity Tips</h4>
              <div class="tip-box">
                <i class="pi pi-lightbulb"></i>
                <div>
                  <strong>Pro Tip:</strong> Use keyboard shortcuts daily to save time. Within a
                  week, they'll become second nature and significantly speed up your workflow.
                </div>
              </div>
            </div>
          </div>
        </TabPanel>

        <TabPanel :value="5">
          <div class="guide-section">
            <h3 class="section-title mt-4">
              <i class="pi pi-life-ring mr-2"></i>
              Support & Help
            </h3>
            <div class="guide-item">
              <h4>Support Options</h4>
              <div class="quick-start-cards">
                <div
                  class="quick-start-card clickable"
                  @click="scrollToSupportSection('faq-section')"
                >
                  <div class="card-icon">
                    <i class="pi pi-question-circle hover:text-white"></i>
                  </div>
                  <h5>FAQ</h5>
                  <p>Find answers to frequently asked questions</p>
                  <div class="card-arrow">
                    <i class="pi pi-arrow-right"></i>
                  </div>
                </div>
                <div
                  class="quick-start-card clickable"
                  @click="scrollToSupportSection('contact-support-section')"
                >
                  <div class="card-icon">
                    <i class="pi pi-envelope hover:text-white"></i>
                  </div>
                  <h5>Contact Support</h5>
                  <p>Get in touch with our support team</p>
                  <div class="card-arrow">
                    <i class="pi pi-arrow-right"></i>
                  </div>
                </div>
              </div>
            </div>
            <div class="guide-item">
              <h4>Getting Help</h4>
              <p>Follow these steps when you need assistance:</p>
              <div class="step-guide">
                <div class="step-item">
                  <div class="step-number">1</div>
                  <div class="step-content">
                    <h5>Check This Guide</h5>
                    <p>Search this help guide for answers to common questions</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">2</div>
                  <div class="step-content">
                    <h5>Review Settings</h5>
                    <p>Check the settings panel for configuration options</p>
                  </div>
                </div>
                <div class="step-item">
                  <div class="step-number">3</div>
                  <div class="step-content">
                    <h5>Contact Administrator</h5>
                    <p>Reach out to your system administrator for technical support</p>
                  </div>
                </div>
              </div>
            </div>
            <div id="faq-section" class="guide-item scroll-mt-4">
              <h4>Frequently Asked Questions</h4>
              <div class="faq-item">
                <div class="faq-icon">
                  <i class="pi pi-lock"></i>
                </div>
                <div class="faq-content">
                  <h5>How do I reset my password?</h5>
                  <p>
                    Contact your system administrator to reset your password. They can help you
                    regain access to your account securely.
                  </p>
                </div>
              </div>
              <div class="faq-item">
                <div class="faq-icon">
                  <i class="pi pi-download"></i>
                </div>
                <div class="faq-content">
                  <h5>Can I export my data?</h5>
                  <p>
                    Yes, you can export your retailer development actions and reports in various
                    formats. Look for the export option in each respective section.
                  </p>
                </div>
              </div>
              <div class="faq-item">
                <div class="faq-icon">
                  <i class="pi pi-calendar"></i>
                </div>
                <div class="faq-content">
                  <h5>How often should I update my retailer development actions?</h5>
                  <p>
                    We recommend reviewing and updating your retailer development actions weekly to
                    ensure they remain relevant and achievable. Regular updates help maintain
                    accuracy.
                  </p>
                </div>
              </div>
              <div class="faq-item">
                <div class="faq-icon">
                  <i class="pi pi-users"></i>
                </div>
                <div class="faq-content">
                  <h5>Can I share my retailer development actions with others?</h5>
                  <p>
                    Retailer development action sharing depends on your user permissions. Contact
                    your system administrator to learn about collaboration features available to
                    you.
                  </p>
                </div>
              </div>
            </div>
            <div id="contact-support-section" class="guide-item scroll-mt-4">
              <h4>Contact Support</h4>
              <p class="text-(--p-anthracite-500) mb-4">
                Our support email address has been updated. Please use the following address for all
                enquiries:
              </p>
              <div class="relative group/email mb-4">
                <div
                  class="bg-(--p-primary-500) text-white border-2 border-(--p-primary-300) rounded-xl p-5 shadow-sm"
                >
                  <div class="flex items-center justify-between gap-3">
                    <p class="font-mono text-base sm:text-lg !text-white break-all flex-1 !mb-0">
                      support@mail.myperformance.stellantis.co.uk
                    </p>
                    <Button
                      icon="pi pi-copy"
                      severity="secondary"
                      size="small"
                      rounded
                      text
                      class="shrink-0 opacity-70 hover:opacity-100 transition-opacity duration-200 !text-white hover:!text-black"
                      @click="copySupportEmail"
                      v-tooltip.top="'Copy email address'"
                    />
                  </div>
                </div>
                <div
                  v-if="emailCopied"
                  class="absolute -top-2 right-0 bg-(--p-surface-800) text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg animate-bounce-in z-10"
                >
                  <i class="pi pi-check mr-1 text-white"></i>
                  Copied!
                </div>
              </div>
              <div
                class="flex items-center gap-3 bg-(--p-surface-50) border border-(--p-surface-200) rounded-lg p-4 mb-4"
              >
                <i
                  class="pi pi-exclamation-triangle text-(--p-warning-500) !text-lg mt-0.5 shrink-0"
                ></i>
                <p class="text-(--p-anthracite-500) text-sm leading-relaxed m-0">
                  Please send all support enquiries to this new email address. The previous email
                  address is no longer in use, and enquiries may not receive a response.
                </p>
              </div>
              <div
                class="bg-(--p-surface-50) border border-(--p-surface-200) rounded-lg p-4 mb-4 text-sm"
              >
                <p class="text-(--p-anthracite-500) mb-3 leading-relaxed">
                  If you have a query with any aspect of your MyPerformance account, please email
                  the team at the above email address. All queries will be responded to within two
                  working days.
                </p>
                <p class="text-(--p-anthracite-500) mb-0 leading-relaxed">
                  The MyPerformance Support Team are available Monday – Friday 9:00am – 5:30pm
                  (excluding Bank Holidays).
                </p>
              </div>
            </div>
            <div class="guide-item">
              <h4>Additional Resources</h4>
              <div class="tip-box">
                <i class="pi pi-lightbulb"></i>
                <div>
                  <strong>Quick Tip:</strong> Press <kbd>Ctrl + /</kbd> at any time to reopen this
                  help guide. You can also use the search feature to quickly find specific topics.
                </div>
              </div>
            </div>
          </div>
        </TabPanel>
        </TabPanels>
      </Tabs>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <div class="footer-info">
          <i class="pi pi-info-circle"></i>
          <span>Press <kbd>Ctrl + /</kbd> to reopen this guide anytime</span>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import Button from 'openvue/button'

const visible = ref(false)
const activeTab = ref(0)
const emailCopied = ref(false)

const openGuide = () => {
  visible.value = true
}

const onShow = () => {
  setTimeout(() => {
    const searchInput = document.querySelector('.p-inputtext') as HTMLInputElement
    if (searchInput) {
      searchInput.focus()
    }
  }, 100)
}

const onHide = () => {
  activeTab.value = 0
}

const scrollToSection = (sectionId: string) => {
  const sectionMap: Record<string, number> = {
    'action-plans': 1,
    tracking: 1,
    statements: 2,
    reporting: 2,
    reports: 2,
    notifications: 3,
  }

  if (sectionMap[sectionId] !== undefined) {
    activeTab.value = sectionMap[sectionId]
  }
}

const scrollToSupportSection = async (sectionId: string) => {
  // Switch to Support tab (index 5)
  activeTab.value = 5
  await nextTick()

  // Wait for tab content to render
  setTimeout(() => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, 100)
}

const copySupportEmail = async () => {
  const email = 'support@mail.myperformance.stellantis.co.uk'
  try {
    await navigator.clipboard.writeText(email)
    emailCopied.value = true
    setTimeout(() => {
      emailCopied.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy email:', err)
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  if (!visible.value) return

  if (event.ctrlKey && event.key === 'k') {
    event.preventDefault()
    const searchInput = document.querySelector('.p-inputtext') as HTMLInputElement
    if (searchInput) {
      searchInput.focus()
    }
  }

  if (event.key === 'Escape') {
    visible.value = false
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

defineExpose({
  openGuide,
})
</script>

<style scoped>
.user-guide-dialog :deep(.p-dialog) {
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.user-guide-dialog :deep(.p-dialog-header) {
  background: linear-gradient(135deg, var(--p-primary-500) 0%, var(--p-primary-600) 100%);
  color: white;
  border-radius: 12px 12px 0 0;
  padding: 1.5rem;
}

.user-guide-dialog :deep(.p-dialog-title) {
  font-size: 1.5rem;
  font-weight: 600;
  color: white;
}

.user-guide-dialog :deep(.p-dialog-content) {
  padding: 0;
  max-height: 75vh;
  overflow-y: auto;
}

.user-guide-content {
  padding: 1.5rem;
}

.guide-header {
  background: linear-gradient(135deg, var(--p-primary-50) 0%, var(--p-primary-100) 100%);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--p-primary-200);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.welcome-section {
  flex: 1;
}

.welcome-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--p-primary-700);
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
}

.welcome-subtitle {
  color: var(--p-primary-600);
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
}

.header-stats {
  display: flex;
  gap: 1.5rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--p-primary-600);
  font-weight: 500;
  font-size: 0.875rem;
}

.stat-item i {
  font-size: 1rem;
}

.search-container {
  margin-bottom: 1.5rem;
}

.search-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--p-surface-50);
  border-radius: 12px;
  border: 2px solid var(--p-surface-200);
  transition: all 0.2s ease;
}

.search-wrapper:focus-within {
  border-color: var(--p-primary-500);
  box-shadow: 0 0 0 3px var(--p-primary-100);
}

.search-container .p-input-icon-left {
  flex: 1;
}

.search-icon {
  color: var(--p-primary-500);
  font-size: 1rem;
}

.search-input {
  font-size: 1rem;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
}

.search-input:focus {
  box-shadow: none;
  outline: none;
}

.clear-btn {
  color: var(--p-anthracite-500);
  transition: color 0.2s ease;
}

.clear-btn:hover {
  color: var(--p-anthracite-500);
}

.search-results-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.5rem 1rem;
  background: var(--p-primary-50);
  border-radius: 6px;
  color: var(--p-primary-700);
  font-size: 0.875rem;
  font-weight: 500;
}

.guide-tabs :deep(.p-tablist) {
  background: var(--p-surface-0);
  border-bottom: 2px solid var(--p-surface-200);
  margin-bottom: 1rem;
}

.guide-tabs :deep(.p-tab) {
  padding: 0.75rem 1rem;
  font-weight: 500;
  color: var(--p-anthracite-500);
  border: none;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
}

.guide-tabs :deep(.p-tab[aria-selected='true']) {
  color: var(--p-primary-600);
  border-bottom-color: var(--p-primary-500);
  background: var(--p-primary-50);
}

.guide-tabs :deep(.p-tab:hover) {
  color: var(--p-primary-500);
  background: var(--p-primary-50);
}

.guide-tabs :deep(.p-tabpanels) {
  background: transparent;
  padding: 0;
}

.guide-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--p-primary-600);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

.guide-item {
  margin-bottom: 1.5rem;
}

.scroll-mt-4 {
  scroll-margin-top: 1rem;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-bounce-in {
  animation: bounce-in 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.a11y-reduced-motion .animate-bounce-in {
  animation: none !important;
  opacity: 1 !important;
}

.guide-item h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--p-anthracite-500);
  margin-bottom: 0.5rem;
}

.guide-item h5 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--p-anthracite-500);
  margin-bottom: 0.5rem;
}

.guide-item p {
  color: var(--p-anthracite-500);
  line-height: 1.6;
  margin-bottom: 0.75rem;
}

.guide-item ul,
.guide-item ol {
  color: var(--p-anthracite-500);
  padding-left: 1.5rem;
  margin-bottom: 0.75rem;
}

.guide-item li {
  margin-bottom: 0.25rem;
  line-height: 1.5;
}

.quick-start-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.quick-start-card {
  background: var(--p-surface-0);
  border: 2px solid var(--p-surface-200);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: left;
  position: relative;
  overflow: hidden;
}

.quick-start-card.clickable {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.quick-start-card.clickable::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--p-primary-500), var(--p-primary-300));
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.quick-start-card.clickable:hover::before {
  transform: scaleX(1);
}

.quick-start-card.clickable:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: var(--p-primary-300);
}

.card-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--p-primary-100), var(--p-primary-200));
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.quick-start-card.clickable:hover .card-icon {
  background: linear-gradient(135deg, var(--p-primary-500), var(--p-primary-600));
  transform: scale(1.1);
  transition: all 0.3s ease;
}

.card-icon i {
  font-size: 1.5rem;
  color: var(--p-primary-600);
}

.quick-start-card.clickable:hover .card-icon i {
  color: white;
  transition: color 0.3s ease;
}

.quick-start-card h5 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--p-anthracite-500);
  margin-bottom: 0.5rem;
}

.quick-start-card.clickable:hover h5 {
  color: var(--p-primary-700);
  transition: color 0.3s ease;
}

.quick-start-card p {
  font-size: 0.875rem;
  color: var(--p-anthracite-500);
  margin: 0 0 1rem 0;
  line-height: 1.5;
}

.card-arrow {
  position: absolute;
  top: 1rem;
  right: 1rem;
  color: var(--p-anthracite-500);
}

.quick-start-card.clickable:hover .card-arrow {
  color: var(--p-primary-500);
  transform: translateX(4px);
  transition: all 0.3s ease;
}

.step-guide {
  margin-top: 1rem;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--p-surface-50);
  border-radius: 8px;
  border-left: 4px solid var(--p-primary-200);
}

.step-number {
  width: 32px;
  height: 32px;
  background: var(--p-primary-500);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.step-content h5 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--p-anthracite-500);
  margin: 0 0 0.25rem 0;
}

.step-content p {
  font-size: 0.875rem;
  color: var(--p-anthracite-500);
  margin: 0;
  line-height: 1.4;
}

.tip-box {
  background: #fecd50;
  border: 1px solid #fec035;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1rem;
}

.tip-box i {
  color: #000000;
  font-size: 1.25rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.tip-box div {
  color: #000000;
  line-height: 1.5;
}

.shortcuts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.shortcut-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--p-surface-0);
  border-radius: 8px;
  border: 2px solid var(--p-surface-200);
}

.shortcut-key {
  flex-shrink: 0;
}

.shortcut-item kbd {
  background: linear-gradient(135deg, var(--p-surface-100), var(--p-surface-200));
  border: 2px solid var(--p-surface-300);
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  font-size: 0.875rem;
  color: var(--p-anthracite-500);
  font-weight: 700;
  min-width: 100px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.shortcut-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.shortcut-action {
  color: var(--p-anthracite-500);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.3;
}

.shortcut-category {
  color: var(--p-anthracite-500);
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: var(--p-surface-50);
  border-top: 1px solid var(--p-surface-200);
  border-radius: 0 0 12px 12px;
}

.footer-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--p-anthracite-500);
  font-size: 0.875rem;
}

.footer-info kbd {
  background: var(--p-surface-200);
  border: 1px solid var(--p-surface-300);
  border-radius: 4px;
  padding: 0.125rem 0.375rem;
  font-family: monospace;
  font-size: 0.75rem;
  color: var(--p-anthracite-500);
  font-weight: 600;
}

.footer-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.faq-item {
  background: var(--p-surface-50);
  border: 2px solid var(--p-surface-200);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.faq-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--p-primary-100), var(--p-primary-200));
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.faq-icon i {
  font-size: 1.25rem;
  color: var(--p-primary-600);
}

.faq-content {
  flex: 1;
}

.faq-content h5 {
  color: var(--p-anthracite-500);
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
}

.faq-content p {
  color: var(--p-anthracite-500);
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.tip-box kbd {
  background: rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 0.125rem 0.375rem;
  font-family: monospace;
  font-size: 0.75rem;
  color: #000000;
  font-weight: 600;
}

.no-results {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--p-anthracite-500);
}

.no-results h4 {
  color: var(--p-anthracite-500);
  margin-bottom: 0.5rem;
}

.no-results p {
  margin: 0;
}

.dark-selector .user-guide-dialog :deep(.p-dialog-content) {
  background: var(--p-surface-900);
}

.dark-selector .guide-header {
  background: linear-gradient(135deg, var(--p-primary-900) 0%, var(--p-primary-800) 100%);
  border-color: var(--p-primary-700);
}

.dark-selector .welcome-title {
  color: var(--p-primary-200);
}

.dark-selector .welcome-subtitle {
  color: var(--p-primary-300);
}

.dark-selector .stat-item {
  color: var(--p-primary-300);
}

.dark-selector .search-wrapper {
  background: var(--p-surface-800);
  border-color: var(--p-surface-700);
}

.dark-selector .search-wrapper:focus-within {
  border-color: var(--p-primary-400);
  box-shadow: 0 0 0 3px var(--p-primary-900);
}

.dark-selector .search-results-info {
  background: var(--p-primary-900);
  color: var(--p-primary-200);
}

.dark-selector .guide-tabs :deep(.p-tablist) {
  background: var(--p-surface-800);
  border-bottom-color: var(--p-surface-700);
}

.dark-selector .guide-tabs :deep(.p-tab) {
  color: var(--p-anthracite-500);
}

.dark-selector .guide-tabs :deep(.p-tab[aria-selected='true']) {
  color: var(--p-primary-400);
  background: var(--p-primary-900);
}

.dark-selector .section-title {
  color: var(--p-primary-400);
}

.dark-selector .guide-item h4,
.dark-selector .guide-item h5 {
  color: var(--p-anthracite-500);
}

.dark-selector .guide-item p,
.dark-selector .guide-item ul,
.dark-selector .guide-item ol {
  color: var(--p-anthracite-500);
}

.dark-selector .quick-start-card {
  background: var(--p-surface-800);
  border-color: var(--p-surface-700);
}

.dark-selector .quick-start-card h5 {
  color: var(--p-anthracite-500);
}

.dark-selector .quick-start-card p {
  color: var(--p-anthracite-500);
}

.dark-selector .shortcut-item {
  background: var(--p-surface-800);
  border-color: var(--p-surface-700);
}

.dark-selector .shortcut-item kbd {
  background: var(--p-surface-700);
  border-color: var(--p-surface-600);
  color: var(--p-anthracite-500);
}

.dark-selector .shortcut-item span {
  color: var(--p-anthracite-500);
}

.dark-selector .faq-item {
  background: var(--p-surface-800);
  border-color: var(--p-surface-700);
}

.dark-selector .faq-icon {
  background: linear-gradient(135deg, var(--p-primary-800), var(--p-primary-900));
}

.dark-selector .faq-icon i {
  color: var(--p-primary-300);
}

.dark-selector .faq-content h5 {
  color: var(--p-anthracite-500);
}

.dark-selector .faq-content p {
  color: var(--p-anthracite-500);
}

.dark-selector .tip-box {
  background: #fecd50;
  border-color: #fec035;
}

.dark-selector .tip-box i {
  color: #000000;
}

.dark-selector .tip-box div {
  color: #000000;
}

.dark-selector .tip-box kbd {
  background: rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 0, 0, 0.2);
  color: #000000;
}

.dark-selector .no-results {
  color: var(--p-anthracite-500);
}

.dark-selector .no-results h4 {
  color: var(--p-anthracite-500);
}

.dark-selector .quick-start-card {
  background: var(--p-surface-800);
  border-color: var(--p-surface-700);
}

.dark-selector .quick-start-card.clickable:hover {
  border-color: var(--p-primary-500);
}

.dark-selector .card-icon {
  background: linear-gradient(135deg, var(--p-primary-800), var(--p-primary-900));
}

.dark-selector .card-icon i {
  color: var(--p-primary-300);
}

.dark-selector .quick-start-card.clickable:hover .card-icon {
  background: linear-gradient(135deg, var(--p-primary-500), var(--p-primary-600));
}

.dark-selector .quick-start-card h5 {
  color: var(--p-anthracite-500);
}

.dark-selector .quick-start-card.clickable:hover h5 {
  color: var(--p-primary-300);
}

.dark-selector .quick-start-card p {
  color: var(--p-anthracite-500);
}

.dark-selector .step-item {
  background: var(--p-surface-800);
  border-left-color: var(--p-primary-600);
}

.dark-selector .step-content h5 {
  color: var(--p-anthracite-500);
}

.dark-selector .step-content p {
  color: var(--p-anthracite-500);
}

.dark-selector .shortcut-item {
  background: var(--p-surface-800);
  border-color: var(--p-surface-700);
}

.dark-selector .shortcut-item kbd {
  background: linear-gradient(135deg, var(--p-surface-700), var(--p-surface-800));
  border-color: var(--p-surface-600);
  color: var(--p-anthracite-500);
}

.dark-selector .shortcut-action {
  color: var(--p-anthracite-500);
}

.dark-selector .shortcut-category {
  color: var(--p-anthracite-500);
}

.dark-selector .dialog-footer {
  background: var(--p-surface-800);
  border-top-color: var(--p-surface-700);
}

.dark-selector .footer-info {
  color: var(--p-anthracite-500);
}

.dark-selector .footer-info kbd {
  background: var(--p-surface-700);
  border-color: var(--p-surface-600);
  color: var(--p-anthracite-500);
}

.a11y-high-contrast .section-title {
  color: var(--p-primary-700);
  font-weight: 700;
}

.a11y-high-contrast .guide-item h4,
.a11y-high-contrast .guide-item h5 {
  color: var(--p-anthracite-500);
  font-weight: 700;
}

.a11y-high-contrast .guide-item p,
.a11y-high-contrast .guide-item ul,
.a11y-high-contrast .guide-item ol {
  color: var(--p-anthracite-500);
}

.a11y-high-contrast .shortcut-item kbd {
  border: 2px solid var(--p-surface-900);
  font-weight: 700;
}

.a11y-high-contrast .quick-start-card {
  border: 2px solid var(--p-surface-300);
}

.a11y-high-contrast .faq-item {
  border: 2px solid var(--p-surface-300);
}

.a11y-reduced-motion .quick-start-card {
  transition: none;
}

.a11y-reduced-motion .quick-start-card:hover {
  transform: none;
}

.guide-tabs :deep(.p-tab:focus) {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.shortcut-item:focus-within {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

@media (max-width: 768px) {
  .user-guide-content {
    padding: 1rem;
  }

  .search-container {
    flex-direction: column;
    gap: 0.75rem;
  }

  .search-container .p-input-icon-left {
    width: 100%;
  }

  .quick-start-cards {
    grid-template-columns: 1fr;
  }

  .shortcuts-grid {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 1.125rem;
  }

  .guide-item h4 {
    font-size: 1rem;
  }

  .guide-tabs :deep(.p-tab) {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .user-guide-dialog :deep(.p-dialog) {
    margin: 0.5rem;
    width: calc(100% - 1rem);
  }

  .user-guide-content {
    padding: 0.75rem;
  }

  .quick-start-card {
    padding: 1rem;
  }

  .shortcut-item {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }

  .shortcut-item kbd {
    min-width: auto;
  }
}
</style>
